import { useEffect, useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { toast } from "sonner"

import { api } from "@/api"
import { useAuth } from "@/store/auth"

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000  // 30 minutes
const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"] as const

export function useInactivityLogout() {
  const { token, refreshToken, signOut } = useAuth()
  const navigate = useNavigate()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!token) return

    const logout = async () => {
      stopWatcher()
      if (refreshToken) {
        try {
          await api.POST("/auth/staff/logout/", { body: { refresh: refreshToken } })
        } catch {
          // 401 means session already dead — still clear local state
        }
      }
      signOut()
      toast.info("You were signed out due to inactivity.")
      void navigate({ to: "/sign-in" })
    }

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => void logout(), INACTIVITY_LIMIT_MS)
    }

    const stopWatcher = () => {
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, resetTimer))
      if (timerRef.current) clearTimeout(timerRef.current)
    }

    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, resetTimer, { passive: true })
    )
    resetTimer()

    return stopWatcher
  }, [token, refreshToken, signOut, navigate])
}
